import React, { useState, useEffect } from 'react';
import './Subscription.css';
import { loadStripe } from '@stripe/stripe-js';
import { apiFetch } from '../api';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.b2bcheck.fr';
const stripePromise = loadStripe('pk_test_51SE50bIqbkWPxo1UpFuUDRxSpLjSX5eQCBXVAtO94DbEHY2jL7Ppwxtyf4ayf5wIevnodIDFU18ytTyTBXmNmeSk00PxjQKNv3');

interface SubscriptionData {
  plan: string | null;
  verificationsLeft: number;
  verificationsTotal: number;
  renewalDate: string | null;
}

interface Invoice {
  id: string;
  amount: number;
  currency: string;
  status: string;
  date: string;
  pdfUrl: string;
  hostedUrl: string;
}

interface SubscriptionProps {
  onBack: () => void;
  userEmail: string;
}

const Subscription: React.FC<SubscriptionProps> = ({ onBack, userEmail }) => {
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [showInvoices, setShowInvoices] = useState(false);

  useEffect(() => {
    loadSubscription();
    loadInvoices();

    // Vérifier si on revient de Stripe
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const success = urlParams.get('success');

    if (success && sessionId) {
      verifyPayment(sessionId);
    }
  }, []);

  const verifyPayment = async (sessionId: string) => {
    try {
      const response = await apiFetch(`/api/stripe/verify-session`, {
        method: 'POST',
        body: JSON.stringify({
          sessionId,
          userEmail
        })
      });

      const data = await response.json();

      if (data.success) {
        alert('✅ Abonnement activé avec succès !');
        window.history.replaceState({}, document.title, window.location.pathname);
        loadSubscription();
        loadInvoices();
      }
    } catch (err) {
      console.error('Erreur vérification paiement:', err);
    }
  };

  const loadSubscription = async () => {
    try {
      const response = await apiFetch(`/api/subscription/status/${userEmail}`);
      const data = await response.json();

      if (data.success) {
        setSubscription(data.subscription);
      }
    } catch (err) {
      console.error('Erreur chargement abonnement:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadInvoices = async () => {
    try {
      const response = await apiFetch(`/api/stripe/invoices/${userEmail}`);
      const data = await response.json();

      if (data.success) {
        setInvoices(data.invoices);
      }
    } catch (err) {
      console.error('Erreur chargement factures:', err);
    }
  };

  const handleChangePlan = async (newPlan: string) => {
    if (!window.confirm(`Voulez-vous changer vers le plan ${newPlan} ?`)) return;

    try {
      setLoading(true);

      const response = await apiFetch(`/api/stripe/change-plan`, {
        method: 'POST',
        body: JSON.stringify({
          userEmail,
          newPlan
        })
      });

      const data = await response.json();

      if (data.success) {
        if (data.requiresPayment) {
          window.location.href = data.sessionUrl;
        } else {
          alert(data.message);
          loadSubscription();
        }
      } else {
        alert(data.message || 'Erreur lors du changement de plan');
      }
    } catch (err) {
      console.error('Erreur changement plan:', err);
      alert('Erreur lors du changement de plan');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setLoading(true);

      const response = await apiFetch(`/api/stripe/create-portal-session`, {
        method: 'POST',
        body: JSON.stringify({ userEmail })
      });

      const data = await response.json();

      if (data.success) {
        if (data.url) {
          // Redirection vers le portail Stripe
          window.location.href = data.url;
        } else if (data.cancelled) {
          // Annulation directe (si portail pas configuré)
          alert(data.message);
          loadSubscription();
        }
      } else {
        alert(data.message || 'Erreur lors de l\'accès au portail');
      }
    } catch (err) {
      console.error('Erreur portail:', err);
      alert('Erreur lors de l\'accès au portail');
    } finally {
      setLoading(false);
    }
  };

  const getButtonText = (planName: string) => {
    if (!subscription?.plan) return 'Choisir ce plan';
    if (subscription.plan === planName) return 'Plan actuel';

    const planOrder = ['Démarrage', 'Professionnel', 'Entreprise'];
    const currentIndex = planOrder.indexOf(subscription.plan);
    const newIndex = planOrder.indexOf(planName);

    if (newIndex > currentIndex) return 'Passer à ce plan';
    if (newIndex < currentIndex) return 'Rétrograder';
    return 'Choisir ce plan';
  };

  if (loading) {
    return (
      <div className="subscription-container">
        <div className="loading">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="subscription-container">
      <div className="current-subscription">
        <div className="subscription-card">
          <div className="subscription-header">
            <div className="subscription-badge">
              {subscription?.plan || 'Gratuit'}
            </div>
            <div className="subscription-stats">
              <div className="stat-item">
                <span className="stat-value">{subscription?.verificationsLeft || 0}</span>
                <span className="stat-label">restantes</span>
              </div>
              <div className="stat-divider">/</div>
              <div className="stat-item">
                <span className="stat-value">{subscription?.verificationsTotal || 1}</span>
                <span className="stat-label">ce mois</span>
              </div>
            </div>
          </div>
          <div className="usage">
            <div className="usage-bar">
              <div
                className="usage-fill"
                style={{
                  width: `${Math.min(100, ((subscription?.verificationsLeft || 0) / (subscription?.verificationsTotal || 1)) * 100)}%`
                }}
              ></div>
            </div>
          </div>
          {subscription?.renewalDate && (
            <div className="renewal">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              Renouvellement le {new Date(subscription.renewalDate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
            </div>
          )}

          {subscription?.plan && (
            <div className="subscription-actions">
              <button className="btn-cancel" onClick={handleCancelSubscription}>
                Gérer mon abonnement et mes factures
              </button>
            </div>
          )}
        </div>

        {showInvoices && invoices.length > 0 && (
          <div className="invoices-list">
            <h3>Factures</h3>
            <div className="invoices-table">
              {invoices.map((invoice) => (
                <div key={invoice.id} className="invoice-row">
                  <div className="invoice-info">
                    <span className="invoice-date">
                      {new Date(invoice.date).toLocaleDateString('fr-FR')}
                    </span>
                    <span className="invoice-amount">
                      {invoice.amount.toFixed(2)} {invoice.currency}
                    </span>
                    <span className={`invoice-status ${invoice.status}`}>
                      {invoice.status === 'paid' ? 'Payée' : invoice.status}
                    </span>
                  </div>
                  <div className="invoice-actions">
                    {invoice.pdfUrl && (
                      <a href={invoice.pdfUrl} target="_blank" rel="noopener noreferrer" className="btn-download">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="7 10 12 15 17 10"></polyline>
                          <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                        PDF
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="section-divider"></div>

      <div className="plans-section">
          <h2>{subscription?.plan ? 'Changer d\'offre' : 'Choisissez votre offre'}</h2>
          <p className="plans-subtitle">Des solutions adaptées à vos besoins de vérification</p>
          <div className="plans-grid">
            <div className="plan-card">
              <div className="plan-header">
                <h3>Démarrage</h3>
                <p className="plan-description">Parfait pour débuter</p>
              </div>
              <div className="price">
                <span className="amount">5</span>
                <span className="currency">€</span>
                <span className="period">/mois</span>
              </div>
              <div className="plan-quota">10 vérifications/mois</div>
              <ul className="features">
                <li>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Rapports complets
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Historique 30 jours
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Assistance par courriel
                </li>
              </ul>
              <button
                className={`subscribe-btn ${subscription?.plan === 'Démarrage' ? 'current-plan' : ''}`}
                onClick={() => handleChangePlan('Démarrage')}
                disabled={subscription?.plan === 'Démarrage'}
              >
                {getButtonText('Démarrage')}
              </button>
            </div>

            <div className="plan-card popular">
              <div className="badge">Recommandé</div>
              <div className="plan-header">
                <h3>Professionnel</h3>
                <p className="plan-description">Le meilleur rapport qualité/prix</p>
              </div>
              <div className="price">
                <span className="amount">15</span>
                <span className="currency">€</span>
                <span className="period">/mois</span>
              </div>
              <div className="plan-quota">50 vérifications/mois</div>
              <ul className="features">
                <li>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Rapports détaillés
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Historique illimité
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Support prioritaire
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Export des données
                </li>
              </ul>
              <button
                className={`subscribe-btn ${subscription?.plan === 'Professionnel' ? 'current-plan' : ''}`}
                onClick={() => handleChangePlan('Professionnel')}
                disabled={subscription?.plan === 'Professionnel'}
              >
                {getButtonText('Professionnel')}
              </button>
            </div>

            <div className="plan-card">
              <div className="plan-header">
                <h3>Entreprise</h3>
                <p className="plan-description">Pour les gros volumes</p>
              </div>
              <div className="price">
                <span className="amount">40</span>
                <span className="currency">€</span>
                <span className="period">/mois</span>
              </div>
              <div className="plan-quota">200 vérifications/mois</div>
              <ul className="features">
                <li>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Rapports premium
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Historique illimité
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Support ligne directe
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Export et accès API
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Gestionnaire de compte dédié
                </li>
              </ul>
              <button
                className={`subscribe-btn ${subscription?.plan === 'Entreprise' ? 'current-plan' : ''}`}
                onClick={() => handleChangePlan('Entreprise')}
                disabled={subscription?.plan === 'Entreprise'}
              >
                {getButtonText('Entreprise')}
              </button>
            </div>
          </div>
        </div>
    </div>
  );
};

export default Subscription;
